import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search } from "lucide-react";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

function Leave() {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [activePage, setActivePage] = useState(1);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [staffdata, setstaffdata] = useState([]);

  // Form state for adding
  const [addData, setAddData] = useState({
    staff_id: "",
    name: "",
    type: "",
    apply_date: "",
    from_date: "",
    to_date: "",
    reason: "",
    status: "",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    staff_id: "",
    name: "",
    type: "",
    apply_date: "",
    from_date: "",
    to_date: "",
    reason: "",
    status: "",
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError("No role assigned to the user. Please contact the administrator.");
        setLoadingPrivileges(false);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page.toLowerCase();
          privileges[pageName] = {
            access: page.access,
            ...page.priviallages,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
      }
    } catch (error) {
      console.error("Error fetching user privileges:", error.message);
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Leave page
  const hasPermission = (permission) => {
    const leavePrivileges =
      userPrivileges["leave management"] ||
      userPrivileges["leave"] ||
      userPrivileges["Leave Management"];
      
    if (!leavePrivileges) {
      console.log(`No privileges found for Leave page (checked: leave management, leave, Leave Management)`);
      return false;
    }

    console.log(`Checking permission '${permission}' for Leave:`, leavePrivileges);
    
    if (permission === "access") {
      const accessGranted = leavePrivileges.access === true;
      console.log(`Permission 'access' result:`, accessGranted);
      return accessGranted;
    }

    if (permission in leavePrivileges && leavePrivileges[permission] === false) {
      console.log(`Permission '${permission}' explicitly denied`);
      return false;
    }

    const hasPerm = leavePrivileges.access && (leavePrivileges.all || leavePrivileges[permission]);
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch leave data
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Leave page");
      setError("You do not have permission to view leave records.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/leave`);
      console.log("Leave data fetched:", response.data);
      setResponseData(response.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching leaves:", error.message, error.response?.data);
      setError("Failed to fetch leave records. Please try again.");
      setResponseData([]);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Fetch staff data
  const fetchstaff = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for staff data");
      setError("You do not have permission to view staff data.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/staff`);
      console.log("Staff data fetched:", response.data);
      setstaffdata(response.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching staff data:", error.message, error.response?.data);
      setError("Failed to fetch staff data. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Validate form data
  const validateForm = (data) => {
    if (!data.staff_id) return "Staff ID is required.";
    if (!data.name) return "Name is required.";
    if (!data.type) return "Leave type is required.";
    if (!data.apply_date) return "Apply date is required.";
    if (!data.from_date) return "From date is required.";
    if (!data.to_date) return "To date is required.";
    if (!data.reason) return "Reason is required.";
    if (!data.status) return "Status is required.";
    if (new Date(data.from_date) > new Date(data.to_date)) {
      return "To date must be after from date.";
    }
    return null;
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Leave page");
      setError("You do not have permission to add leave records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formdata = { ...addData };
      console.log("Adding leave with data:", formdata);
      await axios.post(`${Apiurl}/leave`, formdata);
      setAddData({
        staff_id: "",
        name: "",
        type: "",
        apply_date: "",
        from_date: "",
        to_date: "",
        reason: "",
        status: "",
      });
      fetchData();
      closeAddModal();
    } catch (error) {
      console.error("Error adding leave:", error.message, error.response?.data);
      setError("Failed to add leave. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Leave page");
      setError("You do not have permission to edit leave records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Updating leave with ID:", editingId, "Data:", updateData);
      await axios.put(`${Apiurl}/leave/${editingId}`, updateData);
      fetchData();
      closeEditModal();
    } catch (error) {
      console.error("Error updating leave:", error.message, error.response?.data);
      setError("Failed to update leave. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Edit leave
  const handleEdit = (id, item) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Leave page");
      setError("You do not have permission to edit leave records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid leave ID:", id);
      setError("Invalid leave ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Edit Leave modal for ID:", id, "Item:", item);
    setEditingId(id);
    setUpdateData({
      staff_id: item.staff_id || "",
      name: item.name || "",
      type: item.type || "",
      apply_date: item.apply_date || "",
      from_date: item.from_date || "",
      to_date: item.to_date || "",
      reason: item.reason || "",
      status: item.status || "",
    });
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Leave page");
      setError("You do not have permission to delete leave records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid leave ID:", id);
      setError("Invalid leave ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Leave modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Leave modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Handle delete leave
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Leave page");
      setError("You do not have permission to delete leave records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid leave ID:", deleteId);
      setError("Invalid leave ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting leave with ID:", deleteId);
      await axios.delete(`${Apiurl}/leave/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting leave:", error.message, error.response?.data);
      setError("Failed to delete leave. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Leave page");
      setError("You do not have permission to add leave records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Leave modal");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    console.log("Closing Add Leave modal");
    setShowAddModal(false);
    setAddData({
      staff_id: "",
      name: "",
      type: "",
      apply_date: "",
      from_date: "",
      to_date: "",
      reason: "",
      status: "",
    });
    setError(null);
  };

  const closeEditModal = () => {
    console.log("Closing Edit Leave modal");
    setShowEditModal(false);
    setEditingId(null);
    setUpdateData({
      staff_id: "",
      name: "",
      type: "",
      apply_date: "",
      from_date: "",
      to_date: "",
      reason: "",
      status: "",
    });
    setError(null);
  };

  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);


      useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

  useEffect(() => {
    if (hasPermission("access")) {
      console.log("Access granted for Leave page");
      if (hasPermission("read")) {
        console.log("Fetching leave and staff data due to read permission");
        fetchData();
        fetchstaff();
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Leave page");
      setError("You do not have permission to access this page.");
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data with useMemo
  const filteredData = useMemo(() => {
    return responseData.filter(
      (item) =>
        (item.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.apply_date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.from_date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.to_date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(searchTerm.toLowerCase())) ??
        false
    );
  }, [responseData, searchTerm]);

  // Update items per page when showEntries changes
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
    setCurrentPage(1);
    setActivePage(1);
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", activePage - 1, activePage, activePage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      console.log("Navigating to page:", page);
      setActivePage(page);
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return <div className="text-center py-5">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log("Access denied for role_id:", localStorage.getItem("role_id"), "on Leave page");
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3">
        {hasPermission("create") && (
          <div className="text-end">
            <button
              className="btn btn-sm text-white"
              style={{ backgroundColor: "#01AEBE" }}
              onClick={openAddModal}
            >
              + Add Leave
            </button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mt-2">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        {hasPermission("read") && (
          <div className="card card-body mt-2">
            <div className="bg-white rounded shadow-sm">
              <div className="row mb-3 crm-table-toolbar">
                <div className="col-md-2">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-table"></i>
                    </span>
                    <select
                      className="form-select"
                      value={showEntries}
                      onChange={(e) => {
                        console.log("Changing items per page to:", e.target.value);
                        setShowEntries(e.target.value);
                      }}
                    >
                      <option value="All">Show All</option>
                      {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                        <option key={n} value={n}>
                          Show {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-7"></div>
                <div className="col-md-3">
                  <div className="d-flex align-items-center">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control pe-5"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => {
                          console.log("Search term changed to:", e.target.value);
                          setSearchTerm(e.target.value);
                        }}
                        style={{ width: "250px", paddingRight: "2.5rem" }}
                      />
                      <Search
                        size={16}
                        className="position-absolute text-muted"
                        style={{
                          right: "10px",
                          top: "35%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {loading && <div className="text-center py-3">Loading...</div>}

              <div className="table-responsive">
                <table className="table responsive-table">
                  <thead className="table-light">
                    <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
                      <th>S.No</th>
                      <th>Staff ID</th>
                      <th>Name</th>
                      <th>Apply Date</th>
                      <th>From Date</th>
                      <th>To Date</th>
                      <th>Reason</th>
                      <th>Status</th>
                      {(hasPermission("update") || hasPermission("delete")) && (
                        <th>Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => (
                        <tr key={item._id || index} className="align-middle">
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>{item.staff_id || "-"}</td>
                          <td>{item.name || "-"}</td>
                          <td>{item.apply_date || "-"}</td>
                          <td>{item.from_date || "-"}</td>
                          <td>{item.to_date || "-"}</td>
                          <td>{item.reason || "-"}</td>
                          <td>{item.status || "-"}</td>
                          {(hasPermission("update") ||
                            hasPermission("delete")) && (
                            <td>
                              {hasPermission("delete") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() => openDeleteModal(item._id)}
                                >
                                  <Trash2 size={16} className="text-danger" />
                                </button>
                              )}
                              {hasPermission("update") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() => handleEdit(item._id, item)}
                                >
                                  <Edit size={16} className="text-primary" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            hasPermission("update") || hasPermission("delete")
                              ? 9
                              : 8
                          }
                          className="text-center py-3"
                        >
                          {loading ? "Loading..." : "No leave records found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredData.length > 0 && (
                <div className="container">
                  <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      onClick={() => {
                        if (activePage > 1) {
                          setActivePage(activePage - 1);
                          setCurrentPage(activePage - 1);
                        }
                      }}
                      disabled={activePage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                      <span className="d-none d-sm-inline">Previous</span>
                    </button>

                    <div
                      className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                      style={{ maxWidth: "100%" }}
                    >
                      {pages.map((page, index) => (
                        <button
                          key={index}
                          className={`btn btn-sm px-3 flex-shrink-0 ${
                            page === activePage
                              ? "text-white"
                              : "btn-outline-secondary"
                          }`}
                          style={
                            page === activePage
                              ? { backgroundColor: "#20c997", border: "none" }
                              : {}
                          }
                          onClick={() => handlePageClick(page)}
                          disabled={page === "..."}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      onClick={() => {
                        if (activePage < totalPages) {
                          setActivePage(activePage + 1);
                          setCurrentPage(activePage + 1);
                        }
                      }}
                      disabled={activePage === totalPages}
                    >
                      <span className="d-none d-sm-inline">Next</span>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Leave Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addLeaveModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title text-dark" id="addLeaveModalLabel">
                  Add Leave
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="staff_id"
                          className="form-label fw-bold"
                        >
                          Staff ID
                        </label>
                        <select
                          id="staff_id"
                          name="staff_id"
                          className="form-select"
                          value={addData.staff_id}
                          onChange={(e) => {
                            const selectedStaffId = e.target.value;
                            const selectedStaff = staffdata.find(
                              (staff) => staff.staff_id === selectedStaffId
                            );
                            console.log(
                              "Selected staff ID:",
                              selectedStaffId,
                              selectedStaff
                            );
                            setAddData({
                              ...addData,
                              staff_id: selectedStaffId,
                              name: selectedStaff
                                ? `${selectedStaff.first_name} ${
                                    selectedStaff.last_name || ""
                                  }`.trim()
                                : "",
                            });
                          }}
                          required
                        >
                          <option value="" disabled>
                            Select Staff
                          </option>
                          {staffdata.map((staff) => (
                            <option key={staff._id} value={staff.staff_id}>
                              {staff.staff_id} - {staff.first_name}{" "}
                              {staff.last_name || ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label fw-bold">
                          Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          placeholder="Enter Name"
                          value={addData.name}
                          readOnly
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="type" className="form-label fw-bold">
                          Type
                        </label>
                        <select
                          className="form-select"
                          id="type"
                          name="type"
                          value={addData.type}
                          onChange={(e) => {
                            console.log("Leave type changed:", e.target.value);
                            setAddData({
                              ...addData,
                              type: e.target.value,
                            });
                          }}
                          required
                        >
                          <option value="" disabled>
                            Select Type
                          </option>
                          <option value="Sick Leave">Sick Leave</option>
                          <option value="Casual Leave">Casual Leave</option>
                          <option value="Unplanned Leave">Unplanned Leave</option>
                          <option value="Others">Others</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="apply_date"
                          className="form-label fw-bold"
                        >
                          Apply Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="apply_date"
                          name="apply_date"
                          value={addData.apply_date}
                          onChange={(e) => {
                            console.log("Apply date changed:", e.target.value);
                            setAddData({
                              ...addData,
                              apply_date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="from_date"
                          className="form-label fw-bold"
                        >
                          From Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="from_date"
                          name="from_date"
                          value={addData.from_date}
                          onChange={(e) => {
                            console.log("From date changed:", e.target.value);
                            setAddData({
                              ...addData,
                              from_date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="to_date"
                          className="form-label fw-bold"
                        >
                          To Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="to_date"
                          name="to_date"
                          value={addData.to_date}
                          onChange={(e) => {
                            console.log("To date changed:", e.target.value);
                            setAddData({
                              ...addData,
                              to_date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="reason"
                          className="form-label fw-bold"
                        >
                          Reason
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="reason"
                          name="reason"
                          value={addData.reason}
                          onChange={(e) => {
                            console.log("Reason changed:", e.target.value);
                            setAddData({
                              ...addData,
                              reason: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="status"
                          className="form-label fw-bold"
                        >
                          Status
                        </label>
                        <select
                          className="form-select"
                          id="status"
                          name="status"
                          value={addData.status}
                          onChange={(e) => {
                            console.log("Status changed:", e.target.value);
                            setAddData({
                              ...addData,
                              status: e.target.value,
                            });
                          }}
                          required
                        >
                          <option value="" disabled>
                            Select Status
                          </option>
                          <option value="Pending">Pending</option>
                          <option value="Accept">Accept</option>
                          <option value="Reject">Reject</option>
                          <option value="Others">Others</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeAddModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Leave Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editLeaveModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title text-dark" id="editLeaveModalLabel">
                  Edit Leave
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_staff_id"
                          className="form-label fw-bold"
                        >
                          Staff ID
                        </label>
                        <select
                          className="form-select"
                          name="staff_id"
                          id="edit_staff_id"
                          value={updateData.staff_id}
                          onChange={(e) => {
                            const selectedStaffId = e.target.value;
                            const selectedStaff = staffdata.find(
                              (staff) => staff.staff_id === selectedStaffId
                            );
                            console.log(
                              "Selected staff ID for edit:",
                              selectedStaffId,
                              selectedStaff
                            );
                            setUpdateData({
                              ...updateData,
                              staff_id: selectedStaffId,
                              name: selectedStaff
                                ? `${selectedStaff.first_name} ${
                                    selectedStaff.last_name || ""
                                  }`.trim()
                                : "",
                            });
                          }}
                          required
                        >
                          <option value="" disabled>
                            Select Staff
                          </option>
                          {staffdata.map((staff) => (
                            <option key={staff._id} value={staff.staff_id}>
                              {staff.staff_id} - {staff.first_name}{" "}
                              {staff.last_name || ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_name"
                          className="form-label fw-bold"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="edit_name"
                          name="name"
                          placeholder="Enter Name"
                          value={updateData.name}
                          readOnly
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_type"
                          className="form-label fw-bold"
                        >
                          Type
                        </label>
                        <select
                          className="form-select"
                          id="edit_type"
                          name="type"
                          value={updateData.type}
                          onChange={(e) => {
                            console.log(
                              "Leave type changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              type: e.target.value,
                            });
                          }}
                          required
                        >
                          <option value="" disabled>
                            Select Type
                          </option>
                          <option value="Sick Leave">Sick Leave</option>
                          <option value="Casual Leave">Casual Leave</option>
                          <option value="Unplanned Leave">Unplanned Leave</option>
                          <option value="Others">Others</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_apply_date"
                          className="form-label fw-bold"
                        >
                          Apply Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="edit_apply_date"
                          name="apply_date"
                          value={updateData.apply_date}
                          onChange={(e) => {
                            console.log(
                              "Apply date changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              apply_date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_from_date"
                          className="form-label fw-bold"
                        >
                          From Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="edit_from_date"
                          name="from_date"
                          value={updateData.from_date}
                          onChange={(e) => {
                            console.log(
                              "From date changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              from_date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_to_date"
                          className="form-label fw-bold"
                        >
                          To Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="edit_to_date"
                          name="to_date"
                          value={updateData.to_date}
                          onChange={(e) => {
                            console.log(
                              "To date changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              to_date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_reason"
                          className="form-label fw-bold"
                        >
                          Reason
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="edit_reason"
                          name="reason"
                          value={updateData.reason}
                          onChange={(e) => {
                            console.log(
                              "Reason changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              reason: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="edit_status"
                          className="form-label fw-bold"
                        >
                          Status
                        </label>
                        <select
                          className="form-select"
                          id="edit_status"
                          name="status"
                          value={updateData.status}
                          onChange={(e) => {
                            console.log(
                              "Status changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              status: e.target.value,
                            });
                          }}
                          required
                        >
                          <option value="" disabled>
                            Select Status
                          </option>
                          <option value="Pending">Pending</option>
                          <option value="Accept">Accept</option>
                          <option value="Reject">Reject</option>
                          <option value="Others">Others</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeEditModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteLeaveModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteLeaveModalLabel">
                  Delete Leave
                </h5>
                <p style={{ marginTop: "10px" }}>
                  You're going to delete this leave record. Are you sure?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                >
                  Yes, Delete It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
}

export default Leave;